import * as React from 'react';

import { useBoolean, useGetState, useUpdateEffect } from 'ahooks';
import {
  Button,
  Dropdown,
  Flex,
  Form,
  Input,
  notification,
  Space,
  Tag,
  Typography,
} from 'antd';

import {
  CopyOutlinedIcon,
  DeleteOutlinedIcon,
  MoreOutlinedIcon,
  SnippetsOutlinedIcon,
} from 'components';
import { useAttributeAction, useAttributeValue } from 'context';
import type { AttributeConfigCategory } from 'types';
import { padString, randomId } from 'utils';

const DEFAULT_SHOW_VALUES_COUNT = 10;

type AttributeFormCategoryProps = AttributeConfigCategory & {
  copiedText: string | null;
  handleCopyToClipboard: (text: string) => void;
};

const AttributeFormCategory = ({
  id,
  title,
  values: defaultValues,
  copiedText,
  handleCopyToClipboard,
}: AttributeFormCategoryProps) => {
  const [values, setValues] = useGetState(defaultValues ?? []);
  const [currentValue, setCurrentValue] = useGetState<string>();
  const [showAllValues, { toggle: toggleShowAllValues }] = useBoolean();

  const { categories = [] } = useAttributeValue();
  const action = useAttributeAction();

  const { shownValues, showMoreValues, remainingValuesLength } =
    React.useMemo(() => {
      const totalValues = values.length;
      const showMoreValues = totalValues > DEFAULT_SHOW_VALUES_COUNT;

      const shownValuesLength = showAllValues
        ? totalValues
        : Math.min(totalValues, DEFAULT_SHOW_VALUES_COUNT);
      const remainingValuesLength = totalValues - shownValuesLength;

      return {
        totalValues,
        showMoreValues,
        shownValuesLength,
        remainingValuesLength,
        shownValues: values.slice(0, shownValuesLength),
      };
    }, [values, showAllValues]);

  useUpdateEffect(() => {
    const updatedCategories = categories.map(category => {
      if (category.id === id) {
        return {
          ...category,
          values,
        };
      }
      return category;
    });

    action({
      type: 'UPDATE_ATTRIBUTE_CATEGORIES',
      payload: {
        categories: updatedCategories,
      },
    });
  }, [values]);

  const addToValues = (value: string) => {
    if (values.includes(value)) {
      notification.warning({
        message: 'مقدار وارد شده موجود است.',
      });
    } else {
      setValues(prevValues => [...prevValues, value]);
      setCurrentValue('');
    }
  };

  const removeFromValues = (value: string) => {
    setValues(prevValues =>
      prevValues.filter(prevValue => prevValue !== value),
    );
  };

  return (
    <Space
      direction='vertical'
      size='middle'
      className='w-full overflow-x-auto rounded bg-white px-6 py-8'
    >
      <Flex justify='space-between' align='center'>
        <Typography.Title level={5} className='mb-0'>
          {title}
        </Typography.Title>

        <Dropdown
          menu={{
            items: [
              {
                key: 'copy-category-values',
                label: (
                  <Button
                    type='text'
                    size='small'
                    className='flex items-center px-0'
                    onClick={() => {
                      handleCopyToClipboard(values.join(','));
                    }}
                    disabled={values.length === 0}
                  >
                    <CopyOutlinedIcon />
                    <span className='text-sm'>کپی مقادیر</span>
                  </Button>
                ),
              },
              {
                key: 'paste-category-values',
                label: (
                  <Button
                    type='text'
                    size='small'
                    className='flex items-center px-0'
                    onClick={() => {
                      setValues(prevValues => {
                        if (copiedText) {
                          const newValues = copiedText
                            .split(',')
                            .filter(text => !prevValues.includes(text));

                          return [...prevValues, ...newValues];
                        }

                        return prevValues;
                      });
                    }}
                    disabled={!copiedText}
                  >
                    <SnippetsOutlinedIcon />
                    <span className='text-sm'>جای‌گذاری مقادیر</span>
                  </Button>
                ),
              },
              {
                key: 'delete-attribute-category',
                label: (
                  <Button
                    danger
                    type='link'
                    size='small'
                    className='flex items-center px-0'
                    onClick={() => {
                      const filteredCategories = categories.filter(
                        category => category.id !== id,
                      );

                      action({
                        type: 'UPDATE_ATTRIBUTE_CATEGORIES',
                        payload: {
                          categories: filteredCategories,
                        },
                      });
                    }}
                  >
                    <DeleteOutlinedIcon />
                    <span className='text-sm'>حذف دسته‌‌بندی</span>
                  </Button>
                ),
              },
            ],
          }}
          trigger={['click']}
          placement='bottomLeft'
        >
          <Button
            size='small'
            className='p-4'
            icon={<MoreOutlinedIcon className='text-lg' />}
          />
        </Dropdown>
      </Flex>

      <Form.Item className='mb-0'>
        <Flex gap='small' className='max-sm:flex-col'>
          <Input
            placeholder={`مقدار دسته‌بندی ${title} را وارد کنید`}
            value={currentValue}
            onChange={event => {
              const {
                target: { value },
              } = event;

              setCurrentValue(value);
            }}
            onPressEnter={event => {
              const { key } = event;

              if (key === 'Enter') {
                event.preventDefault();
                if (currentValue) addToValues(currentValue);
              }
            }}
          />
          <Button
            type='primary'
            onClick={() => {
              if (currentValue) {
                addToValues(currentValue);
              }
            }}
          >
            اضافه کردن
          </Button>
        </Flex>
      </Form.Item>

      <Space wrap size={[0, 'small']}>
        <React.Fragment>
          {shownValues.map(value => {
            const id = randomId();
            return (
              <Tag
                key={id}
                closeIcon={true}
                color='blue'
                className='py-0.5'
                onClose={event => {
                  event.preventDefault();
                  removeFromValues(value);
                }}
              >
                {value}
              </Tag>
            );
          })}
        </React.Fragment>

        <React.Fragment>
          {showMoreValues ? (
            <Button
              type='link'
              size='small'
              className='text-xs'
              onClick={toggleShowAllValues}
            >
              {showAllValues
                ? 'مشاهده کمتر'
                : padString(
                    remainingValuesLength.toString(),
                    'padEnd',
                    remainingValuesLength >= 10 ? 3 : 2,
                    '+',
                  )}
            </Button>
          ) : null}
        </React.Fragment>
      </Space>
    </Space>
  );
};

export { AttributeFormCategory };
